import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Container,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCalculator,
  IconFileText,
  IconPlus,
  IconRefresh,
  IconSchool,
  IconTrash,
} from "@tabler/icons-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
} from "../../seo/utils";
import "./GradeCalculatorPage.css";

type NumberValue = number | "";
type GradeMode = "current" | "weighted" | "final" | "test";
type TestAnswerMode = "wrong" | "correct";

type PointsRow = {
  id: string;
  name: string;
  score: NumberValue;
  total: NumberValue;
};

type WeightedCategory = {
  id: string;
  name: string;
  grade: NumberValue;
  weight: NumberValue;
};

const TOOL_PATH = "/tools/grade-calculator";
const TOOLS_PATH = "/tools";

const modeHashes: Record<GradeMode, string> = {
  current: "current-grade-calculator",
  weighted: "weighted-grade-calculator",
  final: "final-grade-calculator",
  test: "test-grade-calculator",
};

const hashModes = Object.fromEntries(
  Object.entries(modeHashes).map(([mode, hash]) => [hash, mode]),
) as Record<string, GradeMode>;

const letterGradeScale = [
  { min: 97, letter: "A+" },
  { min: 93, letter: "A" },
  { min: 90, letter: "A-" },
  { min: 87, letter: "B+" },
  { min: 83, letter: "B" },
  { min: 80, letter: "B-" },
  { min: 77, letter: "C+" },
  { min: 73, letter: "C" },
  { min: 70, letter: "C-" },
  { min: 67, letter: "D+" },
  { min: 63, letter: "D" },
  { min: 60, letter: "D-" },
  { min: 0, letter: "F" },
];

const defaultPointsRows: PointsRow[] = [
  { id: "assignment", name: "Assignment", score: 42, total: 50 },
  { id: "quiz", name: "Quiz", score: 18, total: 20 },
  { id: "exam", name: "Exam", score: 76, total: 100 },
];

const defaultWeightedCategories: WeightedCategory[] = [
  { id: "homework", name: "Homework", grade: 90, weight: 20 },
  { id: "quizzes", name: "Quizzes", grade: 84, weight: 30 },
  { id: "exams", name: "Exams", grade: 88, weight: 50 },
];

const faqs = [
  {
    question: "How do I calculate my grade?",
    answer:
      "To calculate your grade, divide the points you earned by the total possible points and multiply by 100. For example, 45 out of 50 equals 90%.",
  },
  {
    question: "How do I calculate weighted grades?",
    answer:
      "To calculate weighted grades, multiply each category grade by its weight, add the weighted values, and divide by the total weight.",
  },
  {
    question: "How do I calculate my final grade?",
    answer:
      "To calculate your final grade, combine your current grade with your final exam grade based on the final exam weight.",
  },
  {
    question: "What grade do I need on my final exam?",
    answer:
      "You can calculate this by entering your current grade, desired final grade, and final exam weight into the final grade calculator.",
  },
  {
    question: "What is a weighted grade?",
    answer:
      "A weighted grade means different assignments or categories count differently toward your final grade. For example, exams may be worth more than homework.",
  },
  {
    question: "What is my grade if I got 45 out of 50?",
    answer:
      "If you got 45 out of 50, your grade is 90% because 45 divided by 50 multiplied by 100 equals 90%.",
  },
  {
    question: "Can I use this as a test grade calculator?",
    answer:
      "Yes. Use the test grade calculator mode to enter the number of questions and wrong or correct answers to calculate your test percentage.",
  },
  {
    question: "Is this grade calculator accurate?",
    answer:
      "The calculator gives accurate results based on the numbers you enter. However, grading policies vary by school, college, university, and country, so always check your official grading system.",
  },
];

const letterGradeRows = [
  ["97-100%", "A+"],
  ["93-96%", "A"],
  ["90-92%", "A-"],
  ["87-89%", "B+"],
  ["83-86%", "B"],
  ["80-82%", "B-"],
  ["77-79%", "C+"],
  ["73-76%", "C"],
  ["70-72%", "C-"],
  ["67-69%", "D+"],
  ["63-66%", "D"],
  ["60-62%", "D-"],
  ["Below 60%", "F"],
];

export function calculatePointsGrade(rows: PointsRow[]) {
  const validRows = rows.filter(
    (row) => Number(row.score) >= 0 && Number(row.total) > 0,
  );

  const totalScore = validRows.reduce((sum, row) => sum + Number(row.score), 0);
  const totalPossible = validRows.reduce((sum, row) => sum + Number(row.total), 0);

  if (totalPossible <= 0) return null;

  return {
    percentage: (totalScore / totalPossible) * 100,
    totalScore,
    totalPossible,
  };
}

export function calculateWeightedGrade(categories: WeightedCategory[]) {
  const validCategories = categories.filter(
    (item) => Number(item.grade) >= 0 && Number(item.weight) > 0,
  );

  const totalWeight = validCategories.reduce(
    (sum, item) => sum + Number(item.weight),
    0,
  );
  const weightedSum = validCategories.reduce(
    (sum, item) => sum + Number(item.grade) * Number(item.weight),
    0,
  );

  if (totalWeight <= 0) return null;

  return {
    percentage: weightedSum / totalWeight,
    totalWeight,
  };
}

export function calculateRequiredFinalGrade(
  currentGrade: NumberValue,
  desiredGrade: NumberValue,
  finalWeight: NumberValue,
) {
  const current = Number(currentGrade);
  const desired = Number(desiredGrade);
  const weight = Number(finalWeight) / 100;

  if (current < 0 || desired < 0 || weight <= 0 || weight > 1) return null;

  return (desired - current * (1 - weight)) / weight;
}

export function calculateTestGrade(
  totalQuestions: NumberValue,
  answerCount: NumberValue,
  mode: TestAnswerMode = "wrong",
) {
  const total = Number(totalQuestions);
  const count = Number(answerCount);

  if (total <= 0 || count < 0 || count > total) return null;

  const correct = mode === "wrong" ? total - count : count;
  const percentage = (correct / total) * 100;

  return {
    correct,
    total,
    percentage,
  };
}

export function getLetterGrade(percentage: number) {
  return letterGradeScale.find((item) => percentage >= item.min)?.letter || "N/A";
}

export function roundGrade(value: number) {
  return Math.round(value * 100) / 100;
}

function toNumberInputValue(value: string | number): NumberValue {
  if (value === "") {
    return "";
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : "";
}

function numberValue(value: NumberValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatGrade(value: number) {
  const rounded = roundGrade(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatPoints(value: number) {
  const rounded = roundGrade(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function createId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.round(Math.random() * 1000)}`;
}

function getModeFromHash(hash: string): GradeMode {
  const cleanHash = hash.replace(/^#/, "");
  return hashModes[cleanHash] || "current";
}

function GradeCalculatorPage() {
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Grade Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "All",
    url: buildCanonicalUrl(TOOL_PATH),
    description:
      "Free grade calculator to calculate current grade, weighted grade, final exam grade needed, and test score.",
    isAccessibleForFree: true,
  };

  return (
    <Container fluid className="grade-calculator-page" px={0}>
      <Seo
        title="Grade Calculator | Final, Weighted & Test Grade Calculator"
        description="Use our free Grade Calculator to calculate your current grade, weighted grade, test grade, and final exam score needed. Fast, simple, and student-friendly."
        path={TOOL_PATH}
        ogTitle="Free Grade Calculator for Students"
        ogDescription="Calculate your current grade, weighted grade, final exam grade needed, and test score instantly."
        keywords={[
          "grade calculator",
          "final grade calculator",
          "weighted grade calculator",
          "test grade calculator",
          "final exam calculator",
          "grading calculator",
          "calculate my grade",
          "what is my grade",
        ]}
        schema={[
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "Grade Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          webApplicationSchema,
        ]}
      />

      <Box className="grade-calculator-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>Grade Calculator</Text>
            </Breadcrumbs>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
              <Stack gap="md" justify="center">
                <Group gap="sm">
                  <Badge color="green" radius="sm" size="lg">
                    Grade & GPA Tools
                  </Badge>
                  <Badge color="blue" radius="sm" size="lg" variant="light">
                    Free student calculator
                  </Badge>
                </Group>
                <ThemeIcon size={58} radius="md" color="green" variant="light">
                  <IconCalculator size={32} />
                </ThemeIcon>
                <Title order={1} className="grade-calculator-title">
                  Grade Calculator
                </Title>
                <Text size="lg" c="dimmed" className="grade-calculator-intro">
                  Calculate your current grade, weighted grade, final exam grade,
                  or test score using our free student-friendly grade
                  calculator.
                </Text>
              </Stack>

              <Paper
                withBorder
                radius="md"
                p={{ base: "md", md: "xl" }}
                className="grade-calculator-panel"
              >
                <GradeCalculator />
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <main className="grade-calculator-content">
          <GradeCalculatorSeoContent />
          <GradeCalculatorCta />
          <RelatedStudentTools />
          <GradeCalculatorFAQ />
        </main>
      </Container>
    </Container>
  );
}

export function GradeCalculator() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<GradeMode>(() =>
    getModeFromHash(location.hash),
  );

  useEffect(() => {
    setMode(getModeFromHash(location.hash));
  }, [location.hash]);

  function changeMode(nextMode: GradeMode) {
    setMode(nextMode);
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: modeHashes[nextMode],
      },
      { replace: true },
    );
  }

  return (
    <Stack gap="lg" component="section" aria-label="Grade calculator">
      <Stack gap={6}>
        <Title order={2}>Calculate Your Grade</Title>
        <Text c="dimmed">
          Choose the calculator mode that matches your class, test, or final
          exam question.
        </Text>
      </Stack>

      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(value) => changeMode(value as GradeMode)}
        className="grade-calculator-mode-control"
        data={[
          { value: "current", label: "Current Grade Calculator" },
          { value: "weighted", label: "Weighted Grade Calculator" },
          { value: "final", label: "Final Grade Calculator" },
          { value: "test", label: "Test Grade Calculator" },
        ]}
        aria-label="Grade calculator mode"
      />

      {mode === "current" ? <CurrentGradeCalculator /> : null}
      {mode === "weighted" ? <WeightedGradeCalculator /> : null}
      {mode === "final" ? <FinalGradeCalculator /> : null}
      {mode === "test" ? <TestGradeCalculator /> : null}

      <Alert color="blue" radius="md" variant="light" icon={<IconAlertCircle size={18} />}>
        Letter grades can vary by school, college, university, or country.
        Always check your official grading policy.
      </Alert>
    </Stack>
  );
}

function CurrentGradeCalculator() {
  const [rows, setRows] = useState<PointsRow[]>(defaultPointsRows);
  const [allowExtraCredit, setAllowExtraCredit] = useState(false);
  const errors = useMemo(
    () =>
      rows.flatMap((row) => {
        const rowErrors = [
          row.name.trim() ? "" : "Assignment name is required.",
          row.score === "" ? "Score received is required." : "",
          row.total === "" || numberValue(row.total) <= 0
            ? `${row.name || "Assignment"} total possible score must be greater than 0.`
            : "",
          numberValue(row.score) < 0
            ? `${row.name || "Assignment"} score cannot be negative.`
            : "",
        ];

        if (
          !allowExtraCredit &&
          row.score !== "" &&
          row.total !== "" &&
          numberValue(row.score) > numberValue(row.total)
        ) {
          rowErrors.push(
            `${row.name || "Assignment"} score cannot exceed total possible score unless extra credit is allowed.`,
          );
        }

        return rowErrors.filter(Boolean);
      }),
    [allowExtraCredit, rows],
  );
  const result = !errors.length ? calculatePointsGrade(rows) : null;

  function updateRow(id: string, updates: Partial<PointsRow>) {
    setRows((currentRows) =>
      currentRows.map((row) => (row.id === id ? { ...row, ...updates } : row)),
    );
  }

  return (
    <Stack gap="lg" id={modeHashes.current}>
      <Stack gap={4}>
        <Title order={2}>Current Grade Calculator</Title>
        <Text c="dimmed">
          Add assignments, quizzes, exams, projects, or coursework rows to
          calculate your current grade from points.
        </Text>
      </Stack>

      <Stack gap="sm">
        {rows.map((row, index) => (
          <Paper key={row.id} withBorder radius="md" p="md" className="grade-calculator-row">
            <SimpleGrid cols={{ base: 1, md: 4 }} spacing="sm">
              <TextInput
                label="Assignment Name"
                placeholder="Assignment, quiz, exam, project"
                value={row.name}
                onChange={(event) => updateRow(row.id, { name: event.currentTarget.value })}
                aria-label={`Assignment name ${index + 1}`}
              />
              <NumberInput
                label="Score Received"
                placeholder="e.g. 85"
                value={row.score}
                min={0}
                allowNegative={false}
                onChange={(value) => updateRow(row.id, { score: toNumberInputValue(value) })}
                aria-label={`Score received ${index + 1}`}
              />
              <NumberInput
                label="Total Possible Score"
                placeholder="e.g. 100"
                value={row.total}
                min={0}
                allowNegative={false}
                onChange={(value) => updateRow(row.id, { total: toNumberInputValue(value) })}
                aria-label={`Total possible score ${index + 1}`}
              />
              <Button
                mt={{ base: 0, md: 24 }}
                variant="light"
                color="red"
                leftSection={<IconTrash size={16} />}
                disabled={rows.length === 1}
                onClick={() =>
                  setRows((currentRows) =>
                    currentRows.filter((candidate) => candidate.id !== row.id),
                  )
                }
                aria-label={`Remove row ${index + 1}`}
              >
                Remove Row
              </Button>
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>

      <Checkbox
        checked={allowExtraCredit}
        onChange={(event) => setAllowExtraCredit(event.currentTarget.checked)}
        label="Allow extra credit"
      />

      <CalculatorActions
        addLabel="Add Row"
        resetLabel="Reset Calculator"
        onAdd={() =>
          setRows((currentRows) => [
            ...currentRows,
            { id: createId("points"), name: "", score: "", total: "" },
          ])
        }
        onReset={() => {
          setRows(defaultPointsRows);
          setAllowExtraCredit(false);
        }}
      />

      <ValidationAlerts errors={errors} />

      {result ? (
        <GradeResult title="Your Current Grade">
          <Text>
            Your current grade is{" "}
            <strong>{formatGrade(result.percentage)}%</strong>.
          </Text>
          <Text>
            Letter grade:{" "}
            <strong>{getLetterGrade(result.percentage)}</strong>
          </Text>
          <Text>
            You earned <strong>{formatPoints(result.totalScore)}</strong> out of{" "}
            <strong>{formatPoints(result.totalPossible)}</strong> total points.
          </Text>
          {result.percentage > 100 ? (
            <Text c="orange" fw={700}>
              This result is above 100%, likely because extra credit is included.
            </Text>
          ) : null}
        </GradeResult>
      ) : null}
    </Stack>
  );
}

function WeightedGradeCalculator() {
  const [categories, setCategories] = useState<WeightedCategory[]>(
    defaultWeightedCategories,
  );
  const errors = useMemo(
    () =>
      categories.flatMap((category) => {
        const categoryErrors = [
          category.name.trim() ? "" : "Category name is required.",
          category.grade === "" ? "Category grade is required." : "",
          category.weight === "" ? "Category weight is required." : "",
          numberValue(category.grade) < 0 || numberValue(category.grade) > 100
            ? `${category.name || "Category"} grade must be between 0 and 100.`
            : "",
          numberValue(category.weight) <= 0
            ? `${category.name || "Category"} weight must be greater than 0.`
            : "",
        ];

        return categoryErrors.filter(Boolean);
      }),
    [categories],
  );
  const result = !errors.length ? calculateWeightedGrade(categories) : null;

  function updateCategory(id: string, updates: Partial<WeightedCategory>) {
    setCategories((currentCategories) =>
      currentCategories.map((category) =>
        category.id === id ? { ...category, ...updates } : category,
      ),
    );
  }

  return (
    <Stack gap="lg" id={modeHashes.weighted}>
      <Stack gap={4}>
        <Title order={2}>Weighted Grade Calculator</Title>
        <Text c="dimmed">
          Add grade categories and weights from your syllabus to calculate your
          weighted grade.
        </Text>
      </Stack>

      <Stack gap="sm">
        {categories.map((category, index) => (
          <Paper key={category.id} withBorder radius="md" p="md" className="grade-calculator-row">
            <SimpleGrid cols={{ base: 1, md: 4 }} spacing="sm">
              <TextInput
                label="Category Name"
                placeholder="Homework, quiz, midterm, final exam"
                value={category.name}
                onChange={(event) =>
                  updateCategory(category.id, { name: event.currentTarget.value })
                }
                aria-label={`Category name ${index + 1}`}
              />
              <NumberInput
                label="Grade in Category"
                placeholder="e.g. 88"
                value={category.grade}
                min={0}
                max={100}
                allowNegative={false}
                rightSection={<Text size="xs">%</Text>}
                onChange={(value) =>
                  updateCategory(category.id, { grade: toNumberInputValue(value) })
                }
                aria-label={`Grade in category ${index + 1}`}
              />
              <NumberInput
                label="Category Weight"
                placeholder="e.g. 20"
                value={category.weight}
                min={0}
                allowNegative={false}
                rightSection={<Text size="xs">%</Text>}
                onChange={(value) =>
                  updateCategory(category.id, { weight: toNumberInputValue(value) })
                }
                aria-label={`Category weight ${index + 1}`}
              />
              <Button
                mt={{ base: 0, md: 24 }}
                variant="light"
                color="red"
                leftSection={<IconTrash size={16} />}
                disabled={categories.length === 1}
                onClick={() =>
                  setCategories((currentCategories) =>
                    currentCategories.filter(
                      (candidate) => candidate.id !== category.id,
                    ),
                  )
                }
                aria-label={`Remove category ${index + 1}`}
              >
                Remove Category
              </Button>
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>

      <CalculatorActions
        addLabel="Add Category"
        resetLabel="Reset Calculator"
        onAdd={() =>
          setCategories((currentCategories) => [
            ...currentCategories,
            { id: createId("category"), name: "", grade: "", weight: "" },
          ])
        }
        onReset={() => setCategories(defaultWeightedCategories)}
      />

      <ValidationAlerts errors={errors} />

      {result && Math.abs(result.totalWeight - 100) > 0.01 ? (
        <Alert color="yellow" radius="md" variant="light" className="warning">
          Your category weights add up to {formatGrade(result.totalWeight)}%, not
          100%. The result is normalized based on the weights you entered.
        </Alert>
      ) : null}

      {result ? (
        <GradeResult title="Your Weighted Grade">
          <Text>
            Your weighted grade is{" "}
            <strong>{formatGrade(result.percentage)}%</strong>.
          </Text>
          <Text>
            Total weight entered:{" "}
            <strong>{formatGrade(result.totalWeight)}%</strong>
          </Text>
          <Text>
            Letter grade: <strong>{getLetterGrade(result.percentage)}</strong>
          </Text>
        </GradeResult>
      ) : null}
    </Stack>
  );
}

function FinalGradeCalculator() {
  const [currentGrade, setCurrentGrade] = useState<NumberValue>(82);
  const [desiredGrade, setDesiredGrade] = useState<NumberValue>(90);
  const [finalWeight, setFinalWeight] = useState<NumberValue>(30);
  const errors = [
    currentGrade === "" ? "Current grade is required." : "",
    desiredGrade === "" ? "Desired final grade is required." : "",
    finalWeight === "" ? "Final exam weight is required." : "",
    numberValue(currentGrade) < 0 ? "Current grade cannot be negative." : "",
    numberValue(desiredGrade) < 0 ? "Desired final grade cannot be negative." : "",
    numberValue(finalWeight) <= 0 || numberValue(finalWeight) > 100
      ? "Final exam weight must be greater than 0 and no more than 100."
      : "",
  ].filter(Boolean);
  const requiredGrade = !errors.length
    ? calculateRequiredFinalGrade(currentGrade, desiredGrade, finalWeight)
    : null;

  return (
    <Stack gap="lg" id={modeHashes.final}>
      <Stack gap={4}>
        <Title order={2}>Final Grade Calculator</Title>
        <Text c="dimmed">
          Calculate what grade you need on your final exam to reach your desired
          course grade.
        </Text>
      </Stack>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <NumberInput
          label="Current Grade"
          placeholder="e.g. 82"
          value={currentGrade}
          min={0}
          allowNegative={false}
          rightSection={<Text size="xs">%</Text>}
          onChange={(value) => setCurrentGrade(toNumberInputValue(value))}
          aria-label="Current grade"
        />
        <NumberInput
          label="Desired Final Grade"
          placeholder="e.g. 90"
          value={desiredGrade}
          min={0}
          allowNegative={false}
          rightSection={<Text size="xs">%</Text>}
          onChange={(value) => setDesiredGrade(toNumberInputValue(value))}
          aria-label="Desired final grade"
        />
        <NumberInput
          label="Final Exam Weight"
          placeholder="e.g. 30"
          value={finalWeight}
          min={0}
          max={100}
          allowNegative={false}
          rightSection={<Text size="xs">%</Text>}
          onChange={(value) => setFinalWeight(toNumberInputValue(value))}
          aria-label="Final exam weight"
        />
      </SimpleGrid>

      <CalculatorActions
        resetLabel="Reset Calculator"
        onReset={() => {
          setCurrentGrade(82);
          setDesiredGrade(90);
          setFinalWeight(30);
        }}
      />

      <ValidationAlerts errors={errors} />

      {requiredGrade !== null ? (
        <GradeResult title="Final Exam Grade Needed">
          {requiredGrade < 0 ? (
            <Text>
              You have already secured your desired grade based on the current
              inputs.
            </Text>
          ) : (
            <Text>
              You need <strong>{formatGrade(requiredGrade)}%</strong> on your
              final exam to finish with{" "}
              <strong>{formatGrade(numberValue(desiredGrade))}%</strong>.
            </Text>
          )}
          {requiredGrade > 100 ? (
            <Text c="orange" fw={700}>
              You would need more than 100% on the final, so the desired final
              grade may not be possible unless extra credit is available.
            </Text>
          ) : null}
        </GradeResult>
      ) : null}
    </Stack>
  );
}

function TestGradeCalculator() {
  const [totalQuestions, setTotalQuestions] = useState<NumberValue>(50);
  const [answerMode, setAnswerMode] = useState<TestAnswerMode>("wrong");
  const [answerCount, setAnswerCount] = useState<NumberValue>(5);
  const errors = [
    totalQuestions === "" || numberValue(totalQuestions) <= 0
      ? "Number of questions must be greater than 0."
      : "",
    answerCount === "" ? "Answer count is required." : "",
    numberValue(answerCount) < 0 ? "Answer count cannot be negative." : "",
    numberValue(answerCount) > numberValue(totalQuestions)
      ? "Answer count cannot be greater than total questions."
      : "",
  ].filter(Boolean);
  const result = !errors.length
    ? calculateTestGrade(totalQuestions, answerCount, answerMode)
    : null;

  return (
    <Stack gap="lg" id={modeHashes.test}>
      <Stack gap={4}>
        <Title order={2}>Test Grade Calculator</Title>
        <Text c="dimmed">
          Enter total questions and either wrong or correct answers to calculate
          your test grade.
        </Text>
      </Stack>

      <SegmentedControl
        value={answerMode}
        onChange={(value) => setAnswerMode(value as TestAnswerMode)}
        data={[
          { value: "wrong", label: "I know wrong answers" },
          { value: "correct", label: "I know correct answers" },
        ]}
        aria-label="Test answer mode"
      />

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label="Number of Questions"
          placeholder="e.g. 50"
          value={totalQuestions}
          min={0}
          allowNegative={false}
          onChange={(value) => setTotalQuestions(toNumberInputValue(value))}
          aria-label="Number of questions"
        />
        <NumberInput
          label={answerMode === "wrong" ? "Number of Wrong Answers" : "Number of Correct Answers"}
          placeholder={answerMode === "wrong" ? "e.g. 5" : "e.g. 45"}
          value={answerCount}
          min={0}
          allowNegative={false}
          onChange={(value) => setAnswerCount(toNumberInputValue(value))}
          aria-label={
            answerMode === "wrong"
              ? "Number of wrong answers"
              : "Number of correct answers"
          }
        />
      </SimpleGrid>

      <CalculatorActions
        resetLabel="Reset Calculator"
        onReset={() => {
          setTotalQuestions(50);
          setAnswerMode("wrong");
          setAnswerCount(5);
        }}
      />

      <ValidationAlerts errors={errors} />

      {result ? (
        <GradeResult title="Your Test Grade">
          <Text>
            You got <strong>{formatPoints(result.correct)}</strong> out of{" "}
            <strong>{formatPoints(result.total)}</strong> correct.
          </Text>
          <Text>
            Your test grade is{" "}
            <strong>{formatGrade(result.percentage)}%</strong>.
          </Text>
          <Text>
            Letter grade: <strong>{getLetterGrade(result.percentage)}</strong>
          </Text>
        </GradeResult>
      ) : null}
    </Stack>
  );
}

function CalculatorActions({
  addLabel,
  resetLabel,
  onAdd,
  onReset,
}: {
  addLabel?: string;
  resetLabel: string;
  onAdd?: () => void;
  onReset: () => void;
}) {
  return (
    <Group gap="sm">
      {onAdd ? (
        <Button variant="light" leftSection={<IconPlus size={18} />} onClick={onAdd}>
          {addLabel}
        </Button>
      ) : null}
      <Button
        variant="outline"
        color="gray"
        leftSection={<IconRefresh size={18} />}
        onClick={onReset}
      >
        {resetLabel}
      </Button>
    </Group>
  );
}

function ValidationAlerts({ errors }: { errors: string[] }) {
  if (!errors.length) {
    return null;
  }

  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" radius="md">
      <ul className="grade-calculator-alert-list">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </Alert>
  );
}

function GradeResult({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box className="grade-calculator-result" aria-live="polite">
      <Stack gap="sm">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </Box>
  );
}

function GradeCalculatorSeoContent() {
  return (
    <>
      <SeoTextSection title="How to Use the Grade Calculator">
        <Text>
          Use the Grade Calculator to calculate your current grade, weighted
          grade, final exam grade needed, or test score. Choose the calculator
          mode that matches your situation, enter your marks or percentages, and
          the tool will instantly estimate your grade.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate Your Grade">
        <Text>
          To calculate your grade, divide the points you earned by the total
          possible points and multiply by 100. For example, if you earned 85
          points out of 100, your grade is 85%.
        </Text>
        <FormulaBox>
          Grade Percentage = (Points Earned / Total Points) x 100
        </FormulaBox>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate Weighted Grades">
        <Text>
          Weighted grades are calculated by multiplying each category grade by
          its weight and then adding the results together. For example, if
          homework is worth 20%, quizzes are worth 30%, and exams are worth 50%,
          each category affects your final grade differently.
        </Text>
        <FormulaBox>
          Weighted Grade = Sum of (Category Grade x Category Weight) / Total
          Weight
        </FormulaBox>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate Final Grade Needed">
        <Text>
          The final grade calculator helps you estimate what score you need on
          your final exam to reach a desired course grade. This is useful when
          your final exam has a specific weight in your overall grade.
        </Text>
        <FormulaBox>
          Required Final Exam Grade = (Desired Grade - Current Grade x (1 -
          Final Weight)) / Final Weight
        </FormulaBox>
      </SeoTextSection>

      <section className="grade-calculator-section">
        <Stack gap="md">
          <Title order={2}>Grade Calculator Examples</Title>
          <Stack gap="sm">
            <Title order={3}>Example 1: Basic Grade Calculation</Title>
            <Text>
              If you scored 45 out of 50 on a test, your grade is 90% because
              45 / 50 x 100 = 90%.
            </Text>
            <Title order={3}>Example 2: Weighted Grade Calculation</Title>
            <Text>
              If your homework grade is 90% with a 20% weight and your exam
              grade is 80% with an 80% weight, your weighted grade is 82%.
            </Text>
            <Title order={3}>Example 3: Final Exam Grade Needed</Title>
            <Text>
              If your current grade is 85%, your desired grade is 90%, and your
              final exam is worth 30%, you need about 101.67% on the final exam.
              This means your desired grade may not be possible without extra
              credit.
            </Text>
          </Stack>
        </Stack>
      </section>

      <LetterGradeScaleTable />

      <SeoTextSection title="Who Can Use This Grade Calculator?">
        <Text>
          This calculator is useful for school students, college students,
          university students, teachers, and anyone who wants to calculate grades
          quickly. You can use it for assignments, quizzes, tests, exams,
          projects, coursework, and final grade planning.
        </Text>
      </SeoTextSection>
    </>
  );
}

function SeoTextSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="grade-calculator-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function FormulaBox({ children }: { children: ReactNode }) {
  return <Box className="grade-calculator-formula formula-box">{children}</Box>;
}

function LetterGradeScaleTable() {
  return (
    <section className="grade-calculator-section">
      <Stack gap="md">
        <Title order={2}>Common Letter Grade Scale</Title>
        <Box className="grade-calculator-table-wrap">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Percentage</Table.Th>
                <Table.Th>Letter Grade</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {letterGradeRows.map((row) => (
                <Table.Tr key={row.join("-")}>
                  <Table.Td>{row[0]}</Table.Td>
                  <Table.Td>{row[1]}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
        <Text c="dimmed">
          Letter grades can vary by school, college, university, or country.
          Always check your official grading policy.
        </Text>
      </Stack>
    </section>
  );
}

function GradeCalculatorCta() {
  return (
    <section className="grade-calculator-cta cta-box">
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={54} color="blue">
            <IconSchool size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>Need Help Improving Your Grade?</Title>
            <Text c="dimmed" className="grade-calculator-cta-copy">
              If your current grade is lower than expected, SubjectBuddy can
              connect you with academic support experts who can help with
              assignments, coursework, essays, reports, and exam preparation.
            </Text>
          </Stack>
        </Group>
        <Group gap="sm">
          <Button
            component={Link}
            to="/assignment-help"
            radius="md"
            className="cta-button"
            rightSection={<IconArrowRight size={18} />}
          >
            Get Assignment Help
          </Button>
          <Anchor component={Link} to="/homework-help" className="secondary-link">
            Get Homework Help
          </Anchor>
        </Group>
      </Group>
    </section>
  );
}

function RelatedStudentTools() {
  const relatedItems = [
    {
      title: "Final Grade Calculator",
      href: "/tools/final-grade-calculator",
      description: "Find what score you need on your final exam.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Weighted Grade Calculator",
      href: "/tools/weighted-grade-calculator",
      description: "Calculate grades from categories and syllabus weights.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Test Grade Calculator",
      href: "/tools/test-grade-calculator",
      description: "Calculate a test percentage from correct or wrong answers.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "GPA Calculator",
      href: "/tools/gpa-calculator",
      description: "Estimate GPA from courses, credits, and grade points.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Reading Time Calculator",
      href: "/tools/reading-time-calculator",
      description: "Plan study sessions by estimating reading time.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Speech Time Calculator",
      href: "/tools/speech-time-calculator",
      description: "Estimate speaking time for presentations and scripts.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Assignment Help",
      href: "/assignment-help",
      description: "Get academic support when grades need more than calculation.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Homework Help",
      href: "/homework-help",
      description: "Review study and homework support for difficult coursework.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Exam Help",
      href: "/exam-help",
      description: "Prepare for tests and final exams after checking grade goals.",
      icon: <IconSchool size={20} />,
    },
  ];

  // TODO: Add standalone /tools/weighted-grade-calculator and
  // /tools/test-grade-calculator pages only if they can be made substantial.
  return (
    <section className="grade-calculator-section">
      <Stack gap="md">
        <Title order={2}>Related Student Tools</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {relatedItems.map((item) => (
            <Paper
              key={item.href}
              component={Link}
              to={item.href}
              withBorder
              radius="md"
              p="lg"
              className="grade-calculator-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  {item.icon}
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{item.title}</Text>
                  <Text size="sm" c="dimmed">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function GradeCalculatorFAQ() {
  return (
    <section className="grade-calculator-section">
      <Stack gap="md">
        <Title order={2}>FAQs About Grade Calculator</Title>
        {faqs.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export default GradeCalculatorPage;
